import { useEffect, useState } from 'react';
import { Preferences } from '@capacitor/preferences';
import { enableNativePush, isNativePushEnabled } from '@/lib/native-push';
import { isNative, platform } from '@/lib/platform';
import { useT } from '@/lib/i18n';
import { Icon } from './Icon';

const PROMPT_KEY = 'heritage.nativePush.promptAt';
const PROMPT_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

export function PushEnablePrompt() {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isNative() || platform() !== 'android') return;
    let alive = true;
    const hide = () => {
      if (alive) setVisible(false);
    };
    window.addEventListener('heritage:push-enabled', hide);

    void (async () => {
      if (await isNativePushEnabled()) return;
      const lastPrompt = await Preferences.get({ key: PROMPT_KEY });
      if (Date.now() - Number(lastPrompt.value ?? 0) < PROMPT_INTERVAL_MS) {
        return;
      }
      if (alive) setVisible(true);
    })();

    return () => {
      alive = false;
      window.removeEventListener('heritage:push-enabled', hide);
    };
  }, []);

  if (!visible) return null;

  async function postpone() {
    setVisible(false);
    await Preferences.set({ key: PROMPT_KEY, value: String(Date.now()) });
  }

  async function enable() {
    setBusy(true);
    setError(null);
    try {
      await enableNativePush();
      setVisible(false);
    } catch {
      setError(t('Разрешение на уведомления не выдано.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-heritage-200 bg-surface px-4 py-3 shadow-sm">
      <div className="flex min-w-0 items-start gap-2">
        <span className="rounded-xl bg-heritage-100 p-2 text-heritage-700">
          <Icon name="bell" className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {t('Включить push-уведомления?')}
          </p>
          <p className="mt-0.5 text-xs text-heritage-500">
            {t('Получайте уведомления о новых записях, сообщениях и событиях семьи.')}
          </p>
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          onClick={() => void postpone()}
          disabled={busy}
          className="btn-secondary"
        >
          {t('Позже')}
        </button>
        <button
          type="button"
          onClick={() => void enable()}
          disabled={busy}
          className="btn-primary"
        >
          {busy ? t('Включаем…') : t('Включить')}
        </button>
      </div>
    </section>
  );
}
